// Apps → ASO Intelligence: scopes (saved market samples), the competitor
// listings each snapshot collected, and the text / creative / hook / sequence
// patterns counted over them. Keywords (phase 3) come next —
// see docs/aso-intelligence-tz.ru.md.
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteScope, getRun, listRuns, listScopes, runScope, updateScope,
  type AsoRun, type AsoScope, type RunApp, type RunStatus,
} from '@/api/aso';
import { Button } from '@/components/Button';
import { Select } from '@/components/Select';
import { useCanEdit } from '@/components/EditOnly';
import { useToastStore } from '@/store/toast';
import { ScopeModal } from './aso/ScopeModal';
import { AppModal } from './aso/AppModal';
import { AssetImage } from './aso/AssetImage';
import { SequencesTab, TextPatternsTab, VisualPatternsTab } from './aso/PatternsTabs';
import { TaxonomyTab } from './aso/TaxonomyTab';
import { KeywordsTab } from './aso/KeywordsTab';
import { CompareTab } from './aso/CompareTab';

type Tab = 'scopes' | 'competitors' | 'text' | 'keywords' | 'creative' | 'hooks' | 'sequences' | 'compare' | 'taxonomy';
const TABS: { id: Tab; label: string }[] = [
  { id: 'scopes', label: 'Scopes' },
  { id: 'competitors', label: 'Competitors' },
  { id: 'text', label: 'Text Patterns' },
  { id: 'keywords', label: 'Keywords' },
  { id: 'creative', label: 'Creative Patterns' },
  { id: 'hooks', label: 'Hooks' },
  { id: 'sequences', label: 'Sequences' },
  { id: 'compare', label: 'Compare' },
  { id: 'taxonomy', label: 'Taxonomy' },
];
/** Tabs that look at one snapshot and share the scope / snapshot picker. */
const SNAPSHOT_TABS: Tab[] = ['competitors', 'text', 'keywords', 'creative', 'hooks', 'sequences', 'compare'];

const fmt = (sec: number | null | undefined) => (sec ? new Date(sec * 1000).toLocaleString() : '—');

const RUN_BADGE: Record<RunStatus, string> = {
  queued: 'bg-surface-muted text-content-muted',
  running: 'bg-primary/10 text-primary',
  full: 'bg-success-soft text-success',
  partial: 'bg-warning-soft text-warning',
  failed: 'bg-danger-soft text-danger',
};

function RunBadge({ run }: { run: { status: RunStatus; apps_ok: number; apps_total: number } | null }) {
  if (!run) return <span className="text-xs text-content-subtle">never run</span>;
  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-xs ${RUN_BADGE[run.status]}`}>
      {run.status}{run.apps_total ? ` · ${run.apps_ok}/${run.apps_total}` : ''}
    </span>
  );
}

export function Aso() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'scopes';
  const setTab = (t: Tab) => setParams((p) => { p.set('tab', t); return p; });

  return (
    <div className="max-w-7xl mx-auto p-4 space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-content">ASO Intelligence</h1>
        <p className="text-content-muted text-sm mt-1 max-w-3xl">
          Save a market sample (GEO × search or category × TOP-N), collect what the competitors’ Play listings say and
          show, and keep every snapshot. Patterns, hooks and keywords build on top of this.
        </p>
      </div>
      <div className="flex gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-sm -mb-px border-b-2 ${tab === t.id ? 'border-primary text-primary' : 'border-transparent text-content-muted hover:text-content'}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'scopes' && <ScopesTab />}
      {tab === 'taxonomy' && <TaxonomyTab />}
      {SNAPSHOT_TABS.includes(tab) && (
        <SnapshotPicker>
          {(runId) => (
            <>
              {tab === 'competitors' && <CompetitorsTab runId={runId} />}
              {tab === 'text' && <TextPatternsTab runId={runId} />}
              {tab === 'creative' && <VisualPatternsTab runId={runId} kind="tag" />}
              {tab === 'hooks' && <VisualPatternsTab runId={runId} kind="hook" />}
              {tab === 'sequences' && <SequencesTab runId={runId} />}
              {tab === 'keywords' && <KeywordsTab runId={runId} />}
              {tab === 'compare' && <CompareTab runId={runId} />}
            </>
          )}
        </SnapshotPicker>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/** Scope + snapshot selectors, kept in the URL so a link opens the same view. */
function SnapshotPicker({ children }: { children: (runId: number) => ReactNode }) {
  const [params, setParams] = useSearchParams();
  const scopesQ = useQuery({ queryKey: ['aso-scopes'], queryFn: listScopes });
  const scopes = scopesQ.data ?? [];
  const scopeId = Number(params.get('scope')) || scopes[0]?.id || 0;
  const runsQ = useQuery({ queryKey: ['aso-runs', scopeId], queryFn: () => listRuns(scopeId), enabled: scopeId > 0, refetchInterval: 30_000 });
  const runs = runsQ.data ?? [];
  const runId = Number(params.get('run')) || runs[0]?.id || 0;
  const setParam = (k: string, v: string) => setParams((p) => { if (v) p.set(k, v); else p.delete(k); if (k === 'scope') p.delete('run'); return p; });
  useEffect(() => { if (scopeId && !params.get('scope')) setParam('scope', String(scopeId)); }, [scopeId]);
  const runLabel = (r: AsoRun) => `${fmt(r.started_at)} · ${r.status}${r.apps_total ? ` ${r.apps_ok}/${r.apps_total}` : ''}${r.analysis === 'done' ? ' · analysed' : ''}${r.keywords === 'done' ? ' · keywords' : ''}`;
  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-2">
        <Select value={String(scopeId || '')} options={scopes.map((s) => ({ value: String(s.id), label: s.name }))}
          onChange={(v) => setParam('scope', v)} placeholder="Scope" className="sm:w-72" searchable />
        <Select value={String(runId || '')} options={runs.map((r) => ({ value: String(r.id), label: runLabel(r) }))}
          onChange={(v) => setParam('run', v)} placeholder="Snapshot" className="sm:w-96" />
      </div>
      {scopes.length === 0 && !scopesQ.isLoading && (
        <div className="p-6 text-center text-content-subtle bg-surface border border-border rounded-lg">Create a scope first.</div>
      )}
      {scopeId > 0 && runs.length === 0 && !runsQ.isLoading && (
        <div className="p-6 text-center text-content-subtle bg-surface border border-border rounded-lg">This scope has not run yet.</div>
      )}
      {runId > 0 && children(runId)}
    </div>
  );
}

// ---------------------------------------------------------------------------

function ScopesTab() {
  const canEdit = useCanEdit();
  const qc = useQueryClient();
  const toast = useToastStore((s) => s.push);
  const [editing, setEditing] = useState<AsoScope | null | 'new'>(null);
  const scopesQ = useQuery({ queryKey: ['aso-scopes'], queryFn: listScopes, refetchInterval: 30_000 });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['aso-scopes'] });

  const run = useMutation({
    mutationFn: runScope,
    onSuccess: () => { toast('Run queued — the collector picks it up within a minute'); invalidate(); },
    onError: (e: any) => toast(e?.message ?? 'Failed to start the run'),
  });
  const toggle = useMutation({
    mutationFn: (s: AsoScope) => updateScope(s.id, { enabled: s.enabled !== 1 }),
    onSuccess: invalidate,
    onError: (e: any) => toast(e?.message ?? 'Failed to update the scope'),
  });
  const del = useMutation({
    mutationFn: deleteScope,
    onSuccess: () => { toast('Scope deleted'); invalidate(); },
    onError: (e: any) => toast(e?.message ?? 'Failed to delete the scope'),
  });

  const scopes = scopesQ.data ?? [];
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        {canEdit && <Button onClick={() => setEditing('new')}>+ New scope</Button>}
      </div>
      <div className="bg-surface border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-surface-muted text-xs uppercase tracking-wide text-content-subtle">
            <tr>
              <th className="text-left p-3 font-medium">Scope</th>
              <th className="text-left p-3 font-medium">Sample</th>
              <th className="text-left p-3 font-medium">Locales</th>
              <th className="text-left p-3 font-medium">Schedule</th>
              <th className="text-left p-3 font-medium">Last run</th>
              <th className="text-left p-3 font-medium">Runs</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody>
            {scopesQ.isLoading && <tr><td colSpan={7} className="p-6 text-center text-content-subtle">Loading…</td></tr>}
            {!scopesQ.isLoading && scopes.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-content-subtle">No scopes yet. Create one to start collecting.</td></tr>
            )}
            {scopes.map((s) => (
              <tr key={s.id} className={`border-t border-border hover:bg-surface-muted/40 ${s.enabled ? '' : 'opacity-60'}`}>
                <td className="p-3 align-middle">
                  <div className="font-medium text-content">{s.name}</div>
                  <div className="text-xs text-content-subtle">{s.created_by_name || '—'} · {fmt(s.created_at)}</div>
                </td>
                <td className="p-3 align-middle">
                  <span className="font-mono text-xs bg-surface-muted px-1.5 py-0.5 rounded">{s.geo}</span>{' '}
                  <span className="text-content">{s.kind === 'search' ? `“${s.query}”` : s.query}</span>{' '}
                  <span className="text-content-subtle">TOP-{s.top_n}</span>
                </td>
                <td className="p-3 align-middle text-xs text-content-muted" title={s.locale_list.join(', ')}>
                  {s.locales === 'all' ? `all (${s.locale_list.length})` : s.locale_list.join(', ')}
                </td>
                <td className="p-3 align-middle text-content-muted">{s.schedule}{s.keywords_enabled === 1 && <span className="ml-1 text-[10px] text-primary" title="keyword metrics collected after each snapshot">+kw</span>}</td>
                <td className="p-3 align-middle">
                  <RunBadge run={s.last_run} />
                  <div className="text-xs text-content-subtle mt-0.5">{fmt(s.last_run?.started_at)}</div>
                </td>
                <td className="p-3 align-middle text-content">{s.run_count}</td>
                <td className="p-3 align-middle text-right whitespace-nowrap">
                  {canEdit && (
                    <>
                      <Button size="sm" variant="secondary" disabled={run.isPending || s.last_run?.status === 'queued' || s.last_run?.status === 'running'}
                        onClick={() => run.mutate(s.id)}>Run now</Button>{' '}
                      <button className="text-xs text-content-muted hover:underline ml-2" onClick={() => setEditing(s)}>Edit</button>
                      <button className="text-xs text-content-muted hover:underline ml-2" onClick={() => toggle.mutate(s)}>{s.enabled ? 'Disable' : 'Enable'}</button>
                      <button className="text-xs text-danger hover:underline ml-2" onClick={() => {
                        if (confirm(`Delete scope “${s.name}” and all its runs?`)) del.mutate(s.id);
                      }}>Delete</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <ScopeModal scope={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------

function CompetitorsTab({ runId }: { runId: number }) {
  const runQ = useQuery({
    queryKey: ['aso-run', runId],
    queryFn: () => getRun(runId),
    enabled: runId > 0,
    refetchInterval: (q) => (q.state.data?.run.status === 'running' || q.state.data?.run.status === 'queued' ? 10_000 : false),
  });
  const [openApp, setOpenApp] = useState<number | null>(null);
  const [q, setQ] = useState('');

  const apps = useMemo(() => {
    const all = runQ.data?.apps ?? [];
    const needle = q.trim().toLowerCase();
    return needle ? all.filter((a) => [a.name, a.store_id, a.developer, a.title, a.short_desc].some((v) => v?.toLowerCase().includes(needle))) : all;
  }, [runQ.data, q]);

  return (
    <div className="space-y-3">
      <input className="input sm:max-w-xs" placeholder="Filter apps…" value={q} onChange={(e) => setQ(e.target.value)} />
      {runQ.data && (
        <>
          <RunStatusLine run={runQ.data.run} primary={runQ.data.primary_locale} />
          <div className="bg-surface border border-border rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[980px]">
              <thead className="bg-surface-muted text-xs uppercase tracking-wide text-content-subtle">
                <tr>
                  <th className="text-left p-3 font-medium w-10">#</th>
                  <th className="text-left p-3 font-medium">App</th>
                  <th className="text-left p-3 font-medium">Title ({runQ.data.primary_locale})</th>
                  <th className="text-left p-3 font-medium">Short description</th>
                  <th className="text-left p-3 font-medium">Rating</th>
                  <th className="text-left p-3 font-medium">Shots</th>
                  <th className="text-left p-3 font-medium">Locales</th>
                </tr>
              </thead>
              <tbody>
                {apps.map((a) => <AppRow key={a.app_id} app={a} onOpen={() => setOpenApp(a.app_id)} />)}
                {apps.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-content-subtle">No apps yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
      {openApp !== null && <AppModal runId={runId} appId={openApp} onClose={() => setOpenApp(null)} />}
    </div>
  );
}

function RunStatusLine({ run, primary }: { run: AsoRun; primary: string }) {
  const done = run.tasks_total - run.tasks_pending;
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-content-muted">
      <RunBadge run={run} />
      <span>started {fmt(run.started_at)}</span>
      {run.finished_at && <span>finished {fmt(run.finished_at)}</span>}
      {run.tasks_pending > 0 && <span>collecting: {done}/{run.tasks_total} pages</span>}
      <span>primary locale {primary}</span>
      {run.error && <span className="text-danger">{run.error}</span>}
    </div>
  );
}

function AppRow({ app, onOpen }: { app: RunApp; onOpen: () => void }) {
  return (
    <tr className="border-t border-border hover:bg-surface-muted/40 cursor-pointer" onClick={onOpen}>
      <td className="p-3 align-middle text-content-subtle">{app.role === 'ours' ? '—' : app.rank}</td>
      <td className="p-3 align-middle">
        <div className="flex items-center gap-3">
          <AssetImage hash={app.icon_hash} alt={app.name ?? app.store_id} className="w-10 h-10 rounded-lg object-cover shrink-0" />
          <div className="min-w-0">
            <div className="font-medium text-content truncate max-w-[260px]">
              {app.name ?? app.store_id}
              {app.is_ours === 1 && <span className="ml-2 inline-flex px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[10px]">{app.role === 'ours' ? 'ours · compared' : 'ours'}</span>}
            </div>
            <div className="text-xs text-content-subtle truncate max-w-[260px]">{app.developer ?? '—'}{app.category ? ` · ${app.category}` : ''}</div>
          </div>
        </div>
      </td>
      <td className="p-3 align-middle text-content max-w-[220px]">
        <div className="truncate" title={app.title ?? ''}>{app.title ?? <span className="text-content-subtle">—</span>}</div>
        {app.title && <div className="text-[10px] text-content-subtle">{app.title.length}/30</div>}
      </td>
      <td className="p-3 align-middle text-content-muted max-w-[320px]">
        <div className="truncate" title={app.short_desc ?? ''}>{app.short_desc ?? <span className="text-content-subtle">—</span>}</div>
        {app.short_desc && <div className="text-[10px] text-content-subtle">{app.short_desc.length}/80</div>}
      </td>
      <td className="p-3 align-middle text-content-muted whitespace-nowrap">
        {app.rating != null ? `★ ${app.rating}` : '—'}{app.installs ? <div className="text-[10px] text-content-subtle">{app.installs}</div> : null}
      </td>
      <td className="p-3 align-middle text-content">{app.shots}</td>
      <td className="p-3 align-middle">
        <span className={`text-xs ${app.status === 'failed' ? 'text-danger' : app.status === 'pending' ? 'text-content-subtle' : 'text-content-muted'}`}
          title={app.error ?? ''}>
          {app.status === 'pending' ? 'collecting…' : `${app.locales_ok}/${app.locales_total}`}
        </span>
      </td>
    </tr>
  );
}
